import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusDot } from '../dashboard/schoolEquipmentChrome';
import { surface } from '../ui/surface';
import {
  DEVICE_TYPES,
  SEVERITY,
  SEVERITY_META,
  TYPE_META,
  deviceHref,
  deviceSeverity,
  statusDotTone,
} from './stateMeta';
import { headerStatForType } from '../../lib/fleetHeaderStats';

/**
 * Чип вида Оборудования: «N / X» как в шапке и сводке.
 * При X = 0 цифра серая; при проблемах — warning или critical.
 */
const TypeChip = ({ type, summary, muted }) => {
  const { t } = useTranslation();
  const total = summary?.total || 0;
  if (total === 0) return null;

  const { ok, problem, problemTone } = headerStatForType(summary, type);
  const notConfigured = Number(summary?.connectivity?.not_configured) || 0;
  const notConfiguredOnly = notConfigured > 0 && notConfigured === total;

  const severity = muted || notConfiguredOnly
    ? SEVERITY.MUTED
    : problemTone === 'critical'
      ? SEVERITY.CRITICAL
      : problemTone === 'warning'
        ? SEVERITY.WARNING
        : SEVERITY.OK;
  const meta = SEVERITY_META[severity];
  const problemClass = muted || !problemTone
    ? 'text-muted-400'
    : problemTone === 'critical'
      ? 'text-critical'
      : 'text-warning';

  return (
    <span
      className="inline-flex items-center gap-2 rounded-xl bg-white/[0.04] px-2.5 py-1 text-xs"
      title={t(`equipment.types.${type}`)}
    >
      <span className={`material-icons text-sm ${meta.text}`}>
        {TYPE_META[type].icon}
      </span>
      <span className="text-[11px] text-muted-400">{t(`equipment.typeSingular.${type}`)}</span>
      <span className="inline-flex items-baseline gap-1 tabular-nums">
        <span className={`font-semibold ${muted ? 'text-muted-400' : 'text-positive'}`}>{ok}</span>
        <span className="text-muted-600">/</span>
        <span className={`font-semibold ${problemClass}`}>{problem}</span>
      </span>
      {notConfiguredOnly && (
        <span className="text-muted-400">{t('equipment.chip.notConfigured')}</span>
      )}
    </span>
  );
};

/**
 * Карточка единицы Оборудования: глиф вида, подсветка состояния, детали.
 * Читается с одного взгляда и не требует сопоставления со шапкой таблицы.
 */
const DeviceCard = ({ device, routerIp, muted }) => {
  const { t } = useTranslation();
  const level = deviceSeverity(device, { muted });
  const severity = SEVERITY_META[level];
  const showBar = level === SEVERITY.WARNING || level === SEVERITY.CRITICAL;
  const route = deviceHref(device, routerIp);
  const reasons = device.readiness?.reasons || [];
  const readinessState = device.readiness?.state || 'unknown';

  const body = (
    <>
      {showBar && <span className={`absolute inset-y-0 left-0 w-0.5 ${severity.accent}`} aria-hidden />}

      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ${severity.text}`}>
        <span className="material-icons text-[20px]">{TYPE_META[device.deviceType].icon}</span>
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[11px] text-muted-500">
          {t(`equipment.typeSingular.${device.deviceType}`)}
        </span>
        <span className="mt-0.5 block truncate text-sm font-medium text-white" title={device.name || undefined}>
          {device.name || `#${device.id}`}
        </span>
        <span className="mt-0.5 block truncate font-mono text-[12px] text-muted-500">
          {device.target}
          {device.model ? ` · ${device.model}` : ''}
        </span>

        <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <StatusDot
            tone={statusDotTone(device.connectivity)}
            label={t(`equipment.state.${device.connectivity}`)}
          />
          {device.connectivity !== 'not_configured' && (
            <StatusDot
              tone={statusDotTone(readinessState)}
              label={t(`equipment.readiness.${readinessState}`)}
            />
          )}
        </span>

        {reasons.length > 0 && (
          <span className="mt-1.5 block text-[11px] text-warning">
            {reasons
              .map((reason) => t(`equipment.reasons.${reason.reason}`, { count: reason.count }))
              .join(' · ')}
          </span>
        )}
      </span>

      {route && (
        <span className="material-icons self-start text-[18px] text-muted-600 transition group-hover:text-muted-300">
          open_in_new
        </span>
      )}
    </>
  );

  const shell = `group relative flex h-full gap-3 overflow-hidden rounded-2xl bg-surface-1 px-4 py-3 ${
    showBar ? 'pl-5' : ''
  } ${muted ? 'opacity-60' : ''}`;

  return (
    <li>
      {route ? (
        <Link
          to={route}
          className={`${shell} transition hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400`}
        >
          {body}
        </Link>
      ) : (
        <div className={shell}>{body}</div>
      )}
    </li>
  );
};

/**
 * Одна школа — одна строка. Когда канал школы недоступен, строка читается как
 * одно событие: Оборудование приглушено и не выдаётся за неисправное (ADR-0011).
 */
const SchoolEquipmentRow = ({ school, expanded, onToggle }) => {
  const { t } = useTranslation();
  const channelMuted = school.channel === 'unavailable';
  const hasDevices = (school.devices?.length || 0) > 0;

  return (
    <div className={`relative overflow-hidden rounded-2xl ${surface.card}`}>
      {channelMuted && <span className="absolute inset-y-0 left-0 w-0.5 bg-critical" aria-hidden />}
      <div className={`flex items-center ${channelMuted ? 'pl-1' : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasDevices}
        className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left transition enabled:hover:bg-white/[0.04] disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
            hasDevices ? 'bg-white/[0.04] text-muted-300' : 'text-muted-600'
          }`}
        >
          <span className="material-icons text-[20px]">
            {hasDevices ? (expanded ? 'expand_less' : 'expand_more') : 'remove'}
          </span>
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-white">{school.name}</span>
          <span className="mt-0.5 block truncate text-[12px] text-muted-500">
            {[school.region, school.city].filter(Boolean).join(' · ')}
            {school.routerIp ? (
              <span className="font-mono tabular-nums"> · {school.routerIp}</span>
            ) : null}
          </span>
        </span>

        <StatusDot
          tone={statusDotTone(school.channel)}
          label={t(`equipment.channel.${school.channel}`)}
        />

        <span className="flex flex-wrap items-center gap-2">
          {hasDevices ? (
            DEVICE_TYPES.map((type) => (
              <TypeChip key={type} type={type} summary={school.byType?.[type]} muted={channelMuted} />
            ))
          ) : (
            <span className="text-xs text-muted-500">{t('equipment.notEquipped')}</span>
          )}
        </span>
      </button>
      {school.routerIp && (
        <Link
          to={`/schools/${encodeURIComponent(school.routerIp)}`}
          title={t('map.goToSchool')}
          aria-label={t('map.goToSchool')}
          className="mr-3 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-400 transition hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          <span className="material-icons text-[18px]">north_east</span>
        </Link>
      )}
      </div>

      {expanded && hasDevices && (
        <div className="px-4 pb-4">
          {channelMuted && (
            <p className="mb-3 flex items-start gap-2 rounded-2xl bg-critical/10 px-3 py-2 text-xs text-critical">
              <span className="material-icons text-sm">router</span>
              {t('equipment.channelDownNote')}
            </p>
          )}
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {school.devices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                routerIp={school.routerIp}
                muted={channelMuted}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SchoolEquipmentRow;
